import React, { useState, useEffect, useRef } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, getRemotedbUrl, getLayout, isNotEmpty, getStageName } from '../../lib/ep.js';
import { useThrottle } from '../../lib/hooks.js';
import { stage_categories } from '../../data/bootstrap_stages';

import ActionButton from '../../components/builder/ActionButton';
import ActionLink from '../../components/builder/ActionLink.js';
import ButtonCollection from '../../components/builder/ButtonCollection.js';
import DisabledArea from '../../components/builder/DisabledArea';
import { localizeFields } from '../../components/builder/DynamicFields.js';
import { DynamicForm } from '../../components/builder/DynamicForm.js';
import FieldLabel from '../../components/builder/FieldLabel';
import ImageUploader from '../../components/builder/ImageUploader';
import Input from '../../components/builder/Input';
import Loader from '../../components/builder/Loader';
import SaveButton from '../../components/builder/SaveButton';
import ResponsiveYouTube from '../../components/builder/ResponsiveYouTube';

import AnimatedLockIcon from '../../components/svg/AnimatedLockIcon';
import AnimatedTick from '../../components/svg/AnimatedTick.js';

import { canParticipate } from '../Stage.js';
import LeveledHTMLText from '../../components/front/helpers/LeveledHTMLText.js';
import QRScan, { QrScanInput } from '../../components/ui_items/QRScan.js';
import PunchCardStandalone from '../../components/front/PunchCardStandalone.js';
import { formatTokenTitle } from '../../components/front/loyalty/TokenAmount.js';
import OcrTicketProcess from './OcrTicketProcess.js';

const BackLink = ({ stage, ...props }) => {
	const is_builder = props?.is_builder ?? false;

	return (
		<ActionLink
			intl={props.intl}
			_href={(!is_builder) ? getRemotedbUrl(`/promotions/participate/${props.promotion_id}`) : null}
		>***Back</ActionLink>
	)
};

const Wrapper = ({ stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_card_layer, setDisplayCardLayer ] = useState(false);

	const has_children = (props.children != undefined) ? true : false;
	const is_builder = props?.is_builder ?? false;
	const display_back_link = props?.display_back_link ?? true;
	const display_description = props?.display_description ?? true;

	const classes = ['requirement'];

	let styles = null;
	if(stage.reqs_type != 2 && stage.reqs_type != 4 && stage.reqs_type != 5) classes.push('gui_form_register requirement_with_image');
	if(stage.reqs_type == 4) {
		classes.push('stage_requirements');
	} else if(stage.reqs_type != 5) {
		classes.push('gui_main_text_color_on_bg');
		styles = { flexGrow:'0', padding:'2em 0' };
	}

	const valid_qr = (stage.reqs_type == 5 && (stage?.display_valid_qr || props?.qr_code_correct)) ? true : false;

	let reqs_image = stage.reqs_image;
	let full_reqs_image = stage.full_reqs_image;
	let reqs_description = stage.reqs_description;

	let display_success_tick = false;
	
	if(valid_qr) {
		if(stage?.reqs_description_valid_qr != undefined) {
			reqs_description = stage.reqs_description_valid_qr;
		}

		if(stage?.reqs_image_valid_qr != undefined) {
			reqs_image = stage.reqs_image_valid_qr;
			full_reqs_image = stage.full_reqs_image_valid_qr;
		} else {
			display_success_tick = true;
		}
	}

	if(stage.reqs_type == '7') {
		const default_description = `<h2>${i18n("You have {total_coins} {coin_name}.")}</h2><p>${i18n("Each participation costs you {required_coins} {coin_name}.")}</p>`;
		if(!isNotEmpty(stage, 'reqs_description')) reqs_description = default_description;

		const total_coins = props?.total_coins ?? 0;

		const replaces = {
			total_coins,
			coin_name:formatTokenTitle(props.token, total_coins),
			required_coins:stage.reqs_loyalty_amount?.[0].amount,
		};

		reqs_description = props.intl.formatMessage({ id:reqs_description }, replaces);
	}

	//const is_stampscard = ([stage_categories.ID_STAMPSCARD_CATEGORY.id].includes(parseInt(stage.type))) ? true : false;
	const is_stampscard = false;	// disabled always here to avoid problems when displaying with IA validation tickets in progress

	const with_description = (reqs_description != undefined && reqs_description != "") ? true : false;

	return (
		<div class="flex_cols" style={styles}>
			{(reqs_image != undefined && reqs_image != '' && !display_success_tick) && (
				<div class="flex_col col_1">
					<div class="register_media_flex">
						<img src={full_reqs_image} style={{ maxWidth:'100%' }} alt="" />
					</div>
				</div>
			)}

			<div class="flex_col col_2">
				{(display_success_tick) && (
					<AnimatedTick
						size={80}
						_class='in_requirement'
						display_shadow={false}
					/>
				)}

				{(with_description && display_description) && (
					<article class={`template_module gui_main_text_color_on_bg${(has_children) ? ' gui_not_in_bottom' : ''}`}>
						<LeveledHTMLText>{reqs_description}</LeveledHTMLText>
					</article>
				)}

				{(has_children) && (
					<div class={classes.join(" ")}>
						{props.children}

						{(display_back_link || is_stampscard) && (
							<ButtonCollection _class={{ other:'footer_actions gui_not_in_top_big' }}>
								{(is_stampscard) && (
									<ActionLink
										onClick={() => setDisplayCardLayer(true)}
									>View card</ActionLink>
								)}

								{(display_back_link) && (
									<BackLink
										promotion_id={stage.promotion_id}
										is_builder={is_builder}
										intl={props.intl}
									/>
								)}
							</ButtonCollection>
						)}
					</div>
				)}
			</div>

			{(display_card_layer) && (
				<PunchCardStandalone
					promotion={props.promotion}
					participations={props.participations}
					stage={{ Stage:stage }}
					type='layer'
					is_builder={is_builder}
					backButton={() => setDisplayCardLayer(false)}
				/>
			)}
		</div>
	)
};

const ReqsStage = ({ stage, promotion, is_builder, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ response_data, setResponseData ] = useState(false);
	const [ cta_enabled, setCtaEnable ] = useState(false);

	const validate = (state, cb) => {
		if(props.validate == undefined) {
			if(cb != undefined) cb();
			return;
		}

		props.validate(state, (result, proceed) => {
			setTimeout(() => {
				setResponseData({ ...result, proceed });
				if(cb != undefined) cb();
			}, 1000);
		});
	};

	const openStage = (url) => {
		document.location.href = url;
	};

	useEffect(() => {
		validate({}, () => {});

		return () => { };
	}, []);

	const is_evaluating = (response_data.checks != undefined) ? false : true;

	let completed_stages = 0;

	if(response_data && response_data.status == 1) {
		setTimeout(() => {
			setCtaEnable(true);
		}, 600);
	}

	const layout_props = getLayout(promotion);

	const display_back_link = promotion?.is_multistage ?? false;
	const localized_req_stage = localizeFields(stage.reqs_stage,props.locale);
	return (
		<Wrapper
			stage={stage}
			intl={props.intl}
			promotion={promotion}
			participations={props.participations}
			is_builder={is_builder}
			display_back_link={display_back_link}
		>
			<div class="gui_multiparticipation_landing images_landscape is_list without_context">
				{stage.reqs_stage && localized_req_stage.map((req) => {
					if(!req.stage_id) return null;

					const st = props.stages.find((s) => (s.Stage.id == req.stage_id));
					if(!st)return null;
					
					let prerequisit_label;
					switch(req.score_options) {
						case 'min_score':
							prerequisit_label = props.intl.formatMessage({ id:"Score at least {min_score} points" }, { min_score:req.min_score });
							break;
						case 'completed':
							prerequisit_label = i18n("Complete the game");
							break;
						case '':
						default:
							prerequisit_label = i18n("Have participated");
							break;
					}

					if(req.condition_message != undefined && req.condition_message != '') {
						prerequisit_label = req.condition_message;
					}

					const stage_status = (!is_evaluating && response_data.checks[req.stage_id]) ? true : false;
					if(stage_status) completed_stages++;

					const participation = (props.participations != undefined) ? props.participations[req.stage_id] : null;

					const [open, closed, pending, can_participate_again] = canParticipate(promotion.timezone, participation, st.Stage, is_builder);
					let stage_state;
					if(open) {
						stage_state = (can_participate_again) ? 'open' : 'open_but';
					} else if(closed) {
						stage_state = 'closed'
					} else {
						stage_state = 'pending';
					}

					let classes = ['is_stage_requirement', `is_${stage_state}`];
					let whole_link = (!stage_status && can_participate_again && open) ? true : false;
					let display_participate_link = false;
					if(whole_link && !is_evaluating) classes.push('is_whole_link gui_is_animated');
					if(is_evaluating || !stage_status) {
						classes.push('is_not_completed');
						display_participate_link = true;
					} else {
						classes.push('is_completed');
					}

					return (
						<div key={req.id} class={classes.join(" ")} onClick={(!is_evaluating && !stage_status) ? () => openStage(`/promotions/participate/${stage.promotion_id}/${req.stage_id}`) : null}>
							{(is_evaluating) ? (
								<Loader color={layout_props.color.main_color} />
							) : (
								<div class={'state_icon ' + ((stage_status) ? 'ep-check-list-item greenIcon' : 'ep-delete-circle redIcon')} />
							)}

							<div class="gui_main_text_color_on_bg textual_info without_description">
								<div class="stage_info">
									<h2 class="stage_title">{getStageName(st.Stage, i18n)}</h2>
								</div>

								<div class="state_info gui_not_in_top_tiny">
									<div class="participations_info">
										<span>{i18n("Condition")}: <strong>{prerequisit_label}</strong></span>
								
										{(!is_evaluating && req.score_options == 'min_score') && (
											<div>
											{/* <span style={{marginLeft:'0.8em'}}> */}
											({i18n("Your score")}: {(response_data.scores[req.stage_id] != undefined && response_data.scores[req.stage_id] != "") ? response_data.scores[req.stage_id] : '0'})
											{/* </span> */}
											</div>
										)}
									</div>
								</div>
							</div>

							{(!is_evaluating && stage_status) && (
								<span class="completed_stage" />
							)}

							{(display_participate_link) && (
								<div class="participate_button">
									<ActionLink>Participate</ActionLink>
								</div>
							)}
						</div>
					)
				})}
			</div>

			<div class="main_cta gui_main_text_color_on_bg">
				<div class="completed_with_lock">
					<AnimatedLockIcon
						status={cta_enabled}
					/>

					<FormattedMessage
						id="Completed: {actual}/{total}"
						values={{
							actual:completed_stages,
							total:(stage.reqs_stage != undefined) ? stage.reqs_stage.length : 0,
						}}
					/>
				</div>

				<div class={`flex_button gui_big_button gui_flex_center gui_not_in_top_small${(!cta_enabled) ? ' disabled_button' : ''}`}>
					<SaveButton
						_fullclass=""
						onClick={(cta_enabled) ? response_data.proceed : null}
						autotranslate={false}
					>{props.button_label}</SaveButton>
				</div>
			</div>
		</Wrapper>
	)
};

const StageRequirements = ({ stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ state, setState ] = useState({});
	const [ errors, setCodeError ] = useState({});
	const [ watched, setWatched ] = useState(false);
	const [ ocr, setOcr ] = useState(false);
	const [ ocr_error, setOcrError ] = useState(false);
	const [ scan_error, setScanError ] = useState(false);
	const [ scan_success, setScanSuccess ] = useState(false);
	const [ show_form_errors, setShowFormErrors ] = useState(false);

	const can_submit = useRef(true);
	const dynamicIsValid = useRef(true);

	const is_builder = props?.is_builder ?? false;
	const is_requeriment_available_for_ocr = (stage.reqs_type == 1 || stage.reqs_type == 3) ? true : false;
	const ocr_enabled = (stage.reqs_ocr != undefined && stage.reqs_ocr && is_requeriment_available_for_ocr) ? true : false;

	useEffect(() => {
		const keyUp = (e) => {
			if(e.keyCode == 13) {
				let state;
				setState((old) => {
					state = old;
					return old;
				});
				validate(state);
			}
		}
		window.addEventListener('keyup', keyUp, true);

		return () => {
			window.addEventListener('keyup', keyUp, true);
		}
	}, []);

	useEffect(() => {
		if(stage.reqs_type != 5)return;
		if(scan_success && !is_builder) {
			setTimeout(() => {
				document.location.href = scan_success;
				return;
			}, 1000);
		}

		if(is_builder) {
			setTimeout(() => {
				setScanSuccess(false);
			}, 2000);
		}
	}, [ scan_success ]);

	useEffect(() => {
		if(stage.reqs_type != 5)return;
		if(scan_error) {
			setTimeout(() => {
				setScanError(false);
			}, 3000);
		}
	}, [ scan_error ]);

	const _cb = (cb) => {
		cb();
		return;
	};

	const validate = (state, cb) => {
		if(!can_submit.current || !dynamicIsValid.current) {
			setShowFormErrors(true);
			if(cb != undefined) cb();
			return;
		}
		if(props.validate != undefined) {
			props.validate(state, (result) => {
				getErrorMessage(result.reason, result);
				if(cb != undefined) cb();
			});
		} else {
			if(cb != undefined) cb();
		}
	};

	const getErrorMessage = (reason, data) => {
		switch(reason) {
			/* NOT OCR ERROR MESSAGES - ini */
			case 'code_already_used':
				displayErrorMessage({ code:i18n("This code has already been used") + "." });
				break;

			case 'code_no_exists':
				displayErrorMessage({ code:i18n("Invalid code") + "." });
				break;

			case 'missing_graphic':
				displayErrorMessage({ graphic_info:i18n("The image is mandatory") + "." });
				break;

			case 'missing_stages':
				setResponseData({...data});
				displayErrorMessage({ stage_req:i18n("You are missing some milestones") + "." });
				break;

			case 'invalid_qr':
				displayErrorMessage(i18n("This code is not valid."));
				break;
			/* NOT OCR ERROR MESSAGES - end */

			/* OCR ERROR MESSAGES - ini */
			case 'code_not_found':
				displayErrorMessage(i18n("This image is not valid.") + " " + i18n("Code not found.") + " " + i18n("Verify all the instructions and try with a new image."));
				break;

			case 'ocr_code_already_used':
				displayErrorMessage(i18n("This code has already been used") + ". " + i18n("Try a new code and image."));
				break;

			case 'ocr_text_hash':
			case 'image_already_used':
				displayErrorMessage(i18n("This image has already been used."));
				break;

			case 'conditions_not_fulfilled':
			case 'no_ocr':
				displayErrorMessage(i18n("This image is not valid.") + " " + i18n("Verify all the instructions and try with a new image."));
				break;

			case 'too_similar':
				displayErrorMessage(i18n("This image is not valid.") + " " + i18n("Did you already upload it before?") + " " + i18n("Verify all the instructions and try with a new image."));
				break;

			case 'amount_does_not_fulfill':
				displayErrorMessage(i18n("This image is not valid.") + " " + i18n("Double-check the amount.") + " " + i18n("Verify all the instructions and try with a new image."));
				break;

			case 'price_not_found':
				displayErrorMessage(i18n("This image is not valid.") + " " + i18n("Double-check the amount.") + " " + i18n("Verify all the instructions and try with a new image."));
				break;

			case 'date_not_found':
				displayErrorMessage(i18n("This image is not valid.") + " " + i18n("The date of the ticket could not be detected."));
				break;

			case 'unknown':
				displayErrorMessage(i18n("Unknown error"));
				break;
			/* OCR ERROR MESSAGES - end */
		}
	};

	const displayErrorMessage = (msg) => {
		const _msg = (typeof msg == "string") ? msg : msg.code;
		(ocr_enabled) ? setOcrError(msg) : setCodeError(msg);
	};

	const resetOcrImage = () => {
		setOcrError(false);
		setOcr(false);
		change('graphic_info', '[reset]');
	};
	
	const change = (k, v) => {
		try {	
			if(k == "full_graphic_info") return;

			if(k == "ticket_amount") {
				let validator = new RegExp(/^([\u0660-\u06690-9]|\.|\,)*(\.|\,)[\u0660-\u06690-9]{2,3}$/);

				if(!validator.test(v)) {
					setCodeError({ ...errors, [k]:i18n("Invalid format") });
					setState(s=>({...s,[k]:v}));
					return;
				}
			}
			
			if(stage.reqs_ocr != undefined && stage.reqs_ocr && ((k == 'graphic_info' &&  v != '' && v != '[reset]' && v != null) || (k == 'ticket_amount' && v && state.ocr && state.ocr != -1))) {
				setOcr(-1);
				setOcrError(false);
				//FORCEM ERROR JS
				let data = {
					stage_id:stage.id,
					ticket_amount:state.ticket_amount,
				};
				
				if(k == 'graphic_info') data.image = v;
				if(k == 'ticket_amount' && state.ocr != undefined) {
					data.ticket_amount = v;
					data.text = state.ocr;
				}
				
				if(state.code != undefined) data.code = state.code;
				if(props.is_builder != undefined) data.is_builder = true;
				$.post(getRemotedbUrl(`/stages/prereq_ocr/${stage.promotion_id}`), data , (r) => {
					setOcr(r.status);
					let _reason = false;
					if(!r.status) {
						_reason = r.reason;
						getErrorMessage(r.reason);
					}
					state.ocr = r.text;
					state.hash = r.hash;
					setState(state);
					if(is_builder && props.debugInfo != undefined) props.debugInfo(r);
				}, 'JSON');
			}

			state[k] = v;
			setState(state);
			setCodeError({});
		} catch (error) {
			let type = `ocr_image`;
			let data = {};
			data.error = error;
			data.field = k;
			data.value = k == 'graphic_info' ? '[image]' :v; //imageb64 too long
			data.state = state;
			$.post(`/sweeps/track_error_log/${type}/${stage.promotion_id}`, data,()=>{
				getErrorMessage('unknown error');
				resetOcrImage();
			},'JSON');
		}
	};

	const videoWatched = (event) => {
		setWatched(true);
	};

	const proceed = () => {
		if(props.continue != undefined) props.continue();
	};

	const dynamicForm = (data, is_valid) => {
		change('ticket_extra_data', data)
		dynamicIsValid.current = is_valid;
	};

	const formFileUpload = (base64, field, cb) => {
		$.post(`/promotions/upload_form/${stage.promotion_id}`, { file:base64 }, (response) => {
			if(cb != undefined) cb(response);
		}, 'JSON');
	};

	const reqsfileUpload = (base64, field, cb) => {
		$.post(`/promotions/upload_req_file/${stage.promotion_id}`, { file:base64 }, (response) => {
			if(cb != undefined) cb(response);
		}, 'JSON');
	};

	const assignCodeFromScan = (code) => {
		change('code',code)
	}

	const throttleChange = useThrottle(change, 500);

	let button_label = (isNotEmpty(stage, 'reqs_enter_button_label')) ? stage.reqs_enter_button_label : i18n((stage.reqs_type != 5) ? "Enter" : "Continue");

	const has_errors = Object.keys(errors).length > 0 ? true : false;

	const code_as_scan = (stage.reqs_type == 1 && stage.reqs_code_scan == '1') ? true : false;
	const CodeComponent = code_as_scan ? QrScanInput : Input;

	const common_code_props = {
		_class:{first:true},
		label_props:{
			autotranslate:false,
		},
		name:"code",
		errors:errors,
		change:change,
		error_msg_position_class:'',
		value:state.code,
		is_builder:is_builder
	}

	let code_props = common_code_props;
	if(code_as_scan) {
		code_props.onGetContent = assignCodeFromScan;
		code_props.close_on_scan = true;
	}

	const display_back_link = props?.promotion?.is_multistage ?? false;

	if(ocr_enabled) {
		const is_code_enabled = (stage.reqs_type == 1 && stage.reqs_code_type != 0) ? true : false;
		const is_code_entered = (!is_code_enabled || (state.code != undefined && state.code != '')) ? true : false;
		
		const valid_amount = (stage.reqs_ocr_verify_amount == false || stage.reqs_ocr_verify_amount == undefined) || (stage.reqs_ocr_verify_amount && errors.ticket_amount == undefined) ? true : false;
		
		const enable_upload_ticket_image = (is_code_entered && valid_amount && (!stage.reqs_ocr_verify_amount || (state.ticket_amount != undefined && state.ticket_amount != ''))) ? true : false;

		const default_instructions_placeholder = `<p>${i18n("The photo of the receipt must meet these requirements")}:</p><ul><li>${i18n("Only one receipt appears.")}</li><li>${i18n("The whole receipt appears.")}</li><li>${i18n("All content is readable.")}</li><li>${i18n("The receipt is not wrinkled or folded.")}</li><li>${i18n("The receipt has no stains.")}</li><li>${i18n("The reverse of the receipt is not visible. Avoid backlit photos.")}</li><li>${i18n("Make sure the receipt is in focus.")}</li></ul>`;
		
		can_submit.current = valid_amount && enable_upload_ticket_image && ocr != 0;

		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				is_builder={is_builder}
				display_back_link={display_back_link}
			>
				{(is_code_enabled) && (
					<DisabledArea disabled={(ocr != 0) ? true : false} _class={{other:'gui_not_in_bottom_small' + ((ocr != 0) ? ' gui_remove_pointer_events' : '')}} message={false}>
						<CodeComponent
							{...code_props}
						>{(stage.reqs_code_label != undefined && stage.reqs_code_label != "") ? stage.reqs_code_label : i18n("Enter the code")}</CodeComponent>
					</DisabledArea>
				)}

				{(stage.reqs_ocr_verify_amount) && (
					<Input
						name="ticket_amount"
						errors={errors}
						change={throttleChange}
						disabled={(ocr != 0) ? true : false}
						error_msg_position_class="is_floating"
						value={state.ticket_amount}
						_class={{ first:true, other:`gui_not_in_bottom_small${(is_code_enabled) ? ' gui_not_in_top_small' : ''}` }}
						label_props={{
							autotranslate:false,
						}}
					>{(stage.reqs_ocr_verify_amount_label != undefined && stage.reqs_ocr_verify_amount_label != "") ? stage.reqs_ocr_verify_amount_label : i18n("Enter the amount with decimals (XX.XX)")}</Input>
				)}

				{(stage.reqs_form) && (
					<DynamicForm
						public_form={true}
						form={localizeFields(stage.reqs_form,props.locale)}
						fileUpload={reqsfileUpload}
						onChange={dynamicForm}
						showFormErrors={show_form_errors}
						use_wrapper={false}
					/>
				)}

				<DisabledArea disabled={!enable_upload_ticket_image || !valid_amount} _class={{other:'not_in_top gui_not_in_bottom_small' + ((!enable_upload_ticket_image) ? ' gui_remove_pointer_events' : '')}} message={false}>
					<FieldLabel
						_class={{other:'in_top'}}
						autotranslate={false}
					>{stage?.reqs_code_image_label ?? i18n("Upload an image of the receipt")}</FieldLabel>

					<div id="ocr_image" class="gui_pos_rel field_in_bottom">
						<ImageUploader
							_class={{first:true, other:'big_uploader'}}
							name="graphic_info"
							errors={errors}
							image={state.graphic_info}
							change={(enable_upload_ticket_image) ? change : null}
							dd_size="full_w"
							display_custom_dragging_instructions="Upload an image"
							preview={true}
							is_builder={false}
							is_frontend={true}
							small_input={false}
							enable_actions={false}
							_accepted_formats={['JPEG', 'PNG', 'GIF']}
							_max_weight={10}
							loading_message={(ocr !== false && ocr < 0) ? (i18n("Analyzing image") + "...") : null}
							_resize={{width:1200}}
						/>

						{(ocr_error !== false) && (
							<span class="ocr_results redBg whiteColor is_error">{typeof ocr_error == "string" ? ocr_error :ocr_error.code}</span>
						)}

						{(ocr_error !== false) && (
							<span class="ocr_try_again redColor">{i18n("Click on the image to try again")}</span>
						)}

						{(ocr === false || ocr < 1) && (
							<div
								class="ocr_instructions"
								dangerouslySetInnerHTML={{
									__html:(isNotEmpty(stage, 'reqs_ocr_instructions')) ? stage.reqs_ocr_instructions : default_instructions_placeholder,
								}}
							/>
						)}

						{(ocr !== false && ocr > 0) && (
							<span class="ocr_results is_fullscreen relative_to_container">
								<span class="is_ok original_color_item ep-thumbs-up-empty" />
							</span>
						)}
					</div>
				</DisabledArea>

				<DisabledArea
					disabled={(ocr == 1) ? false : true}
					_class={{other:'not_in_top gui_not_in_bottom_small gui_not_in_top_big' + ((ocr != 1) ? ' gui_remove_pointer_events' : '')}}
					message={false}
				>
					<div class="flex_button gui_big_button gui_flex_center">
						<SaveButton
							_fullclass=""
							onClick={((ocr == 1 && !is_builder)) ? (e, cb) => validate(state, cb) : (e, cb) => _cb(cb)}
							autotranslate={false}
						>{button_label}</SaveButton>
					</div>
				</DisabledArea>
			</Wrapper>
		)
	}

	if(stage.reqs_type == 1) {
		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				is_builder={is_builder}
				display_back_link={display_back_link}
			>
				{(stage.reqs_code_type != 0) && (
					<CodeComponent
						{...code_props}
					>{(stage.reqs_code_label != undefined && stage.reqs_code_label != "") ? stage.reqs_code_label : i18n("Enter the code")}</CodeComponent>
				)}
	
				{stage.reqs_form && (
					<DynamicForm
						public_form={true}
						fileUpload={reqsfileUpload}
						form={localizeFields(stage.reqs_form,props.locale)}
						onChange={dynamicForm}
						showFormErrors={show_form_errors}
						use_wrapper={false}
					/>
				)}

				{(isNotEmpty(stage, 'reqs_graphic_info')) && (
					<ImageUploader
						name="graphic_info"
						errors={errors}
						image={state.graphic_info}
						change={change}
						dd_size="full_w"
						preview={true}
						is_builder={false}
						is_frontend={true}
						small_input={false}
						display_custom_dragging_instructions="Upload a picture"
						_accepted_formats={['JPEG', 'PNG', 'GIF']}
						_max_weight={10}
						_resize={{width:1200}}
					>{(stage.reqs_code_image_label != undefined) ? stage.reqs_code_image_label : "Upload an image of the receipt"}</ImageUploader>
				)}

				<div class="flex_button gui_big_button gui_flex_center gui_not_in_top_big">
					<DisabledArea disabled={has_errors} message="To submit, please first fix the errors">
						<SaveButton
							_fullclass=""
							disabled={has_errors}
							onClick={((!is_builder)) ? (e, cb) => validate(state, cb) : (e, cb) => _cb(cb)}
							autotranslate={false}
						>{button_label}</SaveButton>
					</DisabledArea>
				</div>
			</Wrapper>
		)
	}

	if(stage.reqs_type == 2) {
		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				is_builder={is_builder}
				display_back_link={display_back_link}
			>
				{(!watched) ? (
					<ResponsiveYouTube
						videoId={stage.reqs_video_id}
						onEnd={videoWatched}
						is_portrait={stage?.reqs_video_is_portrait ?? false}
						opts={{ autoplay:0 }}
					/>
				) : (
					<div class="flex_button gui_big_button gui_flex_center gui_not_in_top">
						<ActionButton
							_fullclass=""
							onClick={proceed}
							autotranslate={false}
						>{button_label}</ActionButton>
					</div>
				)}
			</Wrapper>
		)
	}

	if(stage.reqs_type == 3) {
		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				is_builder={is_builder}
				display_back_link={display_back_link}
			>
				{(stage.reqs_form) && (
					<DynamicForm
						public_form={true}
						fileUpload={reqsfileUpload}
						form={localizeFields(stage.reqs_form,props.locale)}
						onChange={dynamicForm}
						showFormErrors={show_form_errors}
						use_wrapper={false}
					/>
				)}

				<ImageUploader
					name="graphic_info"
					errors={errors}
					image={state.graphic_info}
					change={change}
					small_input={true}
					is_builder={false}
					is_frontend={true}
					dd_size="full_w"
					display_custom_dragging_instructions="Upload a picture"
					_accepted_formats={['JPEG', 'PNG', 'GIF']}
					_max_weight={10}
					_resize={{width:1200}}
				>{(stage.reqs_code_image_label != undefined) ? stage.reqs_code_image_label : "Upload an image of the receipt"}</ImageUploader>

				<div class="flex_button gui_big_button gui_flex_center gui_not_in_top_big">
					<DisabledArea disabled={has_errors} message="To submit, please first fix the errors">
						<SaveButton
							_fullclass=""
							disabled={has_errors}
							onClick={((!is_builder)) ? (e, cb) => validate(state, cb) :  (e, cb) => _cb(cb)}
							autotranslate={false}
						>{button_label}</SaveButton>
					</DisabledArea>
				</div>
			</Wrapper>
		)
	}

	if(stage.reqs_type == 8) {
		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				is_builder={is_builder}
				display_back_link={false}
				display_description={false}
			>
				<OcrTicketProcess
					remaining_ocr={props.remaining_ocr}
					stage={stage}
					promotion={props.promotion}
					ocr_ticket={props.ocr_ticket}
					notification_email={props.notification_email}
					button_label={button_label}
					is_builder={is_builder}
					validate={(!is_builder) ? validate : null}
					back_link={(display_back_link) ? <BackLink
						promotion_id={stage.promotion_id}
						is_builder={is_builder}
						intl={props.intl}
					/> : null}
				/>
			</Wrapper>
		)
	}

	if(stage.reqs_type == 4) {
		return (
			<ReqsStage
				stage={stage}
				stages={props.stages}
				promotion={props.promotion}
				is_builder={is_builder}
				validate={props.validate}
				button_label={button_label}
				locale={props.locale}
				intl={props.intl}
			/>
		)
	}

	if(stage.reqs_type == 5) {
		const urlParams = new URLSearchParams(window.location.search);
		state.code = urlParams.get('code');

		const is_dynamic_qr = stage.reqs_qr_type == 'dynamic' ? true : false;
		const is_static_qr = !is_dynamic_qr;

		const valid_code_static = (state.code == stage.reqs_qr_code && is_static_qr) ? true : false;
		const valid_code_dynamic = props?.vars?.valid_dynamic_code ?? false;

		const button_enabled = (valid_code_static || valid_code_dynamic) ? true : false;
		const display_valid_qr = stage?.display_valid_qr ?? false;
		const display_button = (button_enabled || display_valid_qr) ? true : false;

		if(!is_builder && state.code != undefined && !button_enabled) {//Ho controla el PHP => CheckDynamicCodeUseCase i Static

			window.location.href = `/promotions/not_allowed/${stage.promotion_id}/qr_code`;

			return (
				<div class="complete_centered" style={{ minHeight:'200px' }}>
					<Loader />
				</div>
			);
		} 

		if((stage?.reqs_qr_mode ?? '') == 'automatic' && display_button && !is_builder) {
			setTimeout(() => {
				validate(state, () => {});//Without button
			}, 1000);

			return (
				<div class="complete_centered" style={{ minHeight:'200px' }}>
					<Loader />
				</div>
			)
		}

		const validateContentQR = (content_url) => {
			//setScanSuccess(true);
			//return;
			let url = null;
			let code = null;
			try {
				url = new URL(content_url);
				const urlParams = new URLSearchParams(url.search);
				code = urlParams.get('code');
			} catch (error) {
				setScanError(true);
				return;
			}
			
			if(!code) {
				setScanError(true);
				return;
			}

			const url_to_check = `promotions/participate/${stage.promotion_id}/${stage.id}?code=${code}`;
			if(content_url.includes(url_to_check)){
				setScanError(false);
				setScanSuccess(content_url);
			}else{
				setScanError(true)
			}
		}

		if(!has_errors && !display_button) {
			return (
				<Wrapper
					stage={stage}
					promotion={props.promotion}
					participations={props.participations}
					qr_code_correct={button_enabled}
					is_builder={is_builder}
					display_back_link={display_back_link}
				>
					<QRScan
						onGetContent={(content) => validateContentQR(content)}
						scan_success={scan_success}
						scan_error={scan_error}
						is_builder={is_builder}
					/>
				</Wrapper>
			)
		}

		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				qr_code_correct={button_enabled}
				is_builder={is_builder}
				display_back_link={display_back_link}
			>
				{(has_errors) && (
					<div class="with_error_message">{errors}</div>
				)}

				{(display_button) && (
					<div class="flex_button gui_big_button gui_flex_center gui_not_in_top_big">
						<SaveButton
							_fullclass=""
							onClick={((!is_builder)) ? (e, cb) => validate(state, cb) : (e, cb) => _cb(cb)}
							autotranslate={false}
						>{button_label}</SaveButton>
					</div>
				)}
			</Wrapper>
		)
	}

	if(stage.reqs_type == 6) {
		const form = localizeFields(stage.reqs_form ?? [],props.locale);

		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				is_builder={is_builder}
				display_back_link={display_back_link}
				intl={props.intl}
			>
				<div class="__gui_form_register template_module gui_main_text_color_on_bg gui_form">
					<DynamicForm
						public_form={true}
						form={form}
						onChange={dynamicForm}
						fileUpload={formFileUpload}
						showFormErrors={show_form_errors}
						use_wrapper={false}
						field_config={{
							signature:{
								auto_url:`/users/upload/${stage.promotion_id}`,
								auto_type:"custom_fields",
								is_builder:true,
							}
						}}
					/>

					<DisabledArea disabled={false} _class={{other:'not_in_top gui_not_in_bottom_small gui_not_in_top_big'}} message={false}>
						<div class="flex_button gui_big_button gui_flex_center">
							<SaveButton
								_fullclass=""
								onClick={((!is_builder)) ? (e, cb) => validate(state, cb) : (e, cb) => _cb(cb)}
								autotranslate={false}
							>{button_label}</SaveButton>
						</div>
					</DisabledArea>
				</div>
			</Wrapper>
		)
	}

	if(stage.reqs_type == 7) {
		const coin_id = stage?.reqs_loyalty_amount?.[0]?.token_id ?? null;
		const total_coins = parseInt(props?.loyalty?.totals?.[coin_id] ?? 0);
		const required_coins = parseInt(stage?.reqs_loyalty_amount?.[0].amount ?? 0);
		const disabled_button = (props?.loyalty != undefined) ? (required_coins > (props.loyalty?.totals?.[coin_id] ?? 0)) : false;
		const token = props?.loyalty?.tokens?.[coin_id]?.LoyaltyToken ?? { title:'token' };

		return (
			<Wrapper
				stage={stage}
				promotion={props.promotion}
				participations={props.participations}
				is_builder={is_builder}
				display_back_link={display_back_link}
				total_coins={total_coins}
				token={token}
				intl={props.intl}
			>
				<div class="gui_form_register template_module gui_main_text_color_on_bg gui_form">
					<div class="flex_button gui_big_button gui_flex_center">
						<SaveButton
							_fullclass=""
							onClick={((!is_builder)) ? (e, cb) => validate(state, cb) : (e, cb) => _cb(cb)}
							autotranslate={false}
							disabled={(disabled_button) ? true : false}
						>{button_label}</SaveButton>
					</div>

					{(disabled_button) && (
						<div class="gui_full_width gui_text_align_center gui_not_in_top_tiny" style={{ '--text-color':'var(--error-color)' }}>
							{props.intl.formatMessage({
								id:"You don't have enough {coin_name}",
							}, {
								coin_name:formatTokenTitle(token, 2),
							})}
						</div>
					)}
				</div>
			</Wrapper>
		)
	}

	return null;
};

export default injectIntl(StageRequirements);